import { apiRequest } from "../api/client";
import { preparePasteListProductsForBasket } from "./groceryPlatformData";

export const PASTE_LIST_STORAGE_KEY = "femtyz_paste_list_flow";
export const PASTE_LIST_FEATURES_OFFSET = 40;

const PLATFORM_ORDER = ["asda", "tesco", "morrison", "sainsbury"];

const PLATFORM_LABELS = {
  asda: "Asda",
  tesco: "Tesco",
  morrison: "Morrisons",
  sainsbury: "Sainsbury's",
};

export function toQueryListParam(input) {
  const raw = String(input || "").trim();
  if (!raw) return "";
  const parts = raw
    .split(/\r?\n|,|;|•|●|·|–|—|\s-\s|^\-\s+/gm)
    .map((x) => String(x || "").trim())
    .filter(Boolean);
  return Array.from(new Set(parts)).join(",");
}

function groupProviderRowsToProducts(rows) {
  const groupedBySku = new Map();
  (Array.isArray(rows) ? rows : []).forEach((row) => {
    const normalized = normalizeProvider(row);
    const key = String(normalized.pid || normalized.sku || "").trim();
    if (!key || !normalized.title) return;
    if (!groupedBySku.has(key)) groupedBySku.set(key, []);
    groupedBySku.get(key).push(normalized);
  });

  return Array.from(groupedBySku.values())
    .map((group) => {
      const best = pickBestProvider(group);
      if (!best) return null;
      return {
        ...best,
        comparison: buildComparison(group),
      };
    })
    .filter(Boolean);
}

export function expandPasteLines(input) {
  const raw = String(input || "").trim();
  if (!raw) return [];
  const newlineParts = raw
    .split(/\r?\n/)
    .map((line) => String(line || "").trim())
    .filter(Boolean);
  if (newlineParts.length > 1) return newlineParts;
  const singleLine = newlineParts[0] || raw;
  const splitParts = toQueryListParam(singleLine)
    .split(",")
    .map((part) => String(part || "").trim())
    .filter(Boolean);
  if (splitParts.length > 1) return splitParts;
  return newlineParts.length ? newlineParts : splitParts;
}

export function isListPasteMode(lines) {
  return Array.isArray(lines) && lines.length > 1;
}

export async function fetchFeaturesProductsPage(
  dispatch,
  fetchActowizFeatures,
  { queryList, page, pageSize, offer = false }
) {
  const targetStart = (page - 1) * pageSize;
  let collected = [];
  let apiStart = targetStart;
  let totalCount = 0;
  let basketTotals = null;
  let attempts = 0;
  const maxAttempts = 15;

  while (collected.length < pageSize && attempts < maxAttempts) {
    attempts += 1;
    const result = await dispatch(
      fetchActowizFeatures({
        query_list: queryList,
        offer: offer || undefined,
        start: apiStart,
        offset: pageSize,
        mode: "replace",
        force: true,
      })
    ).unwrap();

    if (typeof result?.totalCount === "number") {
      totalCount = result.totalCount;
    }
    if (!basketTotals?.cheapest && result?.basketTotals?.cheapest) {
      basketTotals = result.basketTotals;
    }

    const batch = Array.isArray(result?.items) ? result.items : [];
    const fetchedCount =
      typeof result?.fetchedCount === "number" ? result.fetchedCount : batch.length;

    if (!batch.length) break;

    const seen = new Set(collected.map((p) => String(p?.pid || p?.sku || "")));
    batch.forEach((item) => {
      const key = String(item?.pid || item?.sku || "");
      if (key && seen.has(key)) return;
      if (key) seen.add(key);
      collected.push(item);
    });

    apiStart += Math.max(fetchedCount, 1);
    if (totalCount && apiStart >= totalCount) break;
  }

  return {
    items: collected.slice(0, pageSize),
    totalCount: totalCount || collected.length,
    basketTotals,
  };
}

export async function fetchCatalogProductsPage(
  dispatch,
  fetchActowizProducts,
  { queryList, page, pageSize }
) {
  const providerBatchSize = pageSize * 4;
  let collected = [];
  let apiStart = (page - 1) * providerBatchSize;
  let totalCount = 0;
  let attempts = 0;
  const maxAttempts = 12;

  while (collected.length < pageSize && attempts < maxAttempts) {
    attempts += 1;
    const result = await dispatch(
      fetchActowizProducts({
        query_list: queryList,
        start: apiStart,
        offset: providerBatchSize,
        mode: "replace",
        force: true,
      })
    ).unwrap();

    if (typeof result?.totalCount === "number") {
      totalCount = result.totalCount;
    }

    const flatRows = Array.isArray(result?.items) ? result.items : [];
    if (!flatRows.length) break;

    const grouped = groupProviderRowsToProducts(flatRows);
    const seen = new Set(collected.map((p) => String(p?.pid || p?.sku || "")));
    grouped.forEach((item) => {
      const key = String(item?.pid || item?.sku || "");
      if (key && seen.has(key)) return;
      if (key) seen.add(key);
      collected.push(item);
    });

    apiStart += providerBatchSize;
    const providerTotal = typeof result?.totalCount === "number" ? result.totalCount : 0;
    if (providerTotal && apiStart >= providerTotal) break;
    if (flatRows.length < providerBatchSize) break;
  }

  return {
    items: collected.slice(0, pageSize),
    totalCount: totalCount || collected.length,
  };
}

export function parseListLines(input) {
  const raw = String(input || "").trim();
  if (!raw) return [];
  return raw
    .split(/\r?\n/)
    .map((line) => String(line || "").trim())
    .filter(Boolean);
}

export function loadPasteListFlow() {
  try {
    const raw = sessionStorage.getItem(PASTE_LIST_STORAGE_KEY);
    if (!raw) return null;
    return JSON.parse(raw);
  } catch {
    return null;
  }
}

export function savePasteListFlow(data) {
  try {
    sessionStorage.setItem(PASTE_LIST_STORAGE_KEY, JSON.stringify(data));
  } catch {
    // ignore
  }
}

export function clearPasteListFlow() {
  try {
    sessionStorage.removeItem(PASTE_LIST_STORAGE_KEY);
  } catch {
    // ignore
  }
}

function normalizePlatformKey(platform) {
  const p = String(platform || "").trim().toLowerCase();
  if (p === "morrisons") return "morrison";
  if (p === "sainsburry" || p === "sainsburys") return "sainsbury";
  return p;
}

function parsePrice(value) {
  if (value == null) return null;
  if (typeof value === "number") return Number.isFinite(value) ? value : null;
  const normalized = String(value).trim().replace(",", ".").replace(/[^0-9.-]/g, "");
  if (!normalized) return null;
  const parsed = Number.parseFloat(normalized);
  return Number.isFinite(parsed) ? parsed : null;
}

function normalizeProvider(p) {
  const images = Array.isArray(p?.all_images)
    ? p.all_images
    : p?.main_image_url
    ? [p.main_image_url]
    : p?.image
    ? [p.image]
    : [];
  const response = p?.response && typeof p.response === "object" ? p.response : null;
  const matchTypeRaw =
    p?.match_type ?? p?.matchType ?? response?.match_type ?? response?.matchType ?? "";
  const productNameRaw =
    p?.product_name ?? p?.productName ?? response?.product_name ?? response?.productName ?? "";
  return {
    platform: normalizePlatformKey(p?.platform),
    pid: p?.product_id ?? p?.pid ?? p?.sku,
    sku: p?.sku || p?.product_id,
    title: p?.product_name || p?.title || p?.sku || "",
    product_name: String(productNameRaw || "").trim(),
    price: parsePrice(p?.price),
    match_type: String(matchTypeRaw || "").trim(),
    images,
    image: images[0] || "",
    url: p?.url || p?.product_url || p?.productUrl || null,
  };
}

function buildComparison(providers) {
  const byPlatform = {};
  (Array.isArray(providers) ? providers : []).forEach((provider) => {
    const normalized = normalizeProvider(provider);
    const platform = normalized.platform;
    if (!platform) return;
    const price = normalized.price;
    const prev = byPlatform[platform];
    if (Number.isFinite(price)) {
      if (!prev || !Number.isFinite(prev.price) || price < prev.price) {
        byPlatform[platform] = normalized;
      }
    } else if (!prev) {
      byPlatform[platform] = normalized;
    }
  });

  return PLATFORM_ORDER.map((platform) => ({
    platform,
    label: PLATFORM_LABELS[platform] || platform,
    price: Number.isFinite(byPlatform[platform]?.price) ? byPlatform[platform].price : null,
    product_name: byPlatform[platform]?.product_name || "",
    match_type: byPlatform[platform]?.match_type || "",
    url: byPlatform[platform]?.url || null,
  }));
}

function pickBestProvider(providers) {
  if (!Array.isArray(providers) || !providers.length) return null;
  let best = providers[0];
  let bestPrice = parsePrice(best?.price);
  for (const p of providers) {
    const pr = parsePrice(p?.price);
    if (Number.isFinite(pr) && (!Number.isFinite(bestPrice) || pr < bestPrice)) {
      best = p;
      bestPrice = pr;
    }
  }
  return normalizeProvider(best);
}

export function getMatchConfidence(matchType) {
  const value = String(matchType || "").trim().toLowerCase();
  if (value.includes("exact")) {
    return { label: "Exact", tone: "exact" };
  }
  if (value.includes("higher size")) {
    return { label: "Medium", tone: "medium" };
  }
  if (value.includes("high") || value.includes("close")) {
    return { label: "High", tone: "high" };
  }
  if (value.includes("medium") || value.includes("partial")) {
    return { label: "Medium", tone: "medium" };
  }
  if (value) {
    return { label: "High", tone: "high" };
  }
  return { label: "Medium", tone: "medium" };
}

export function buildMatchesFromLines(lines, products) {
  return lines.map((queryLine, index) => {
    const product = products[index] || null;
    const matchType =
      product?.match_type ||
      product?.comparison?.find((row) => row?.match_type)?.match_type ||
      "";
    const confidence = getMatchConfidence(matchType);
    return {
      id: `${index}-${queryLine}`,
      queryLine,
      product,
      matchedName: product?.product_name || product?.title || "",
      matchType,
      confidence,
      image: product?.image || product?.images?.[0] || "",
      comparison: product?.comparison || [],
    };
  });
}

function normalizeFeatureResults(payload) {
  const results = Array.isArray(payload?.results)
    ? payload.results
    : Array.isArray(payload?.data)
    ? payload.data
    : Array.isArray(payload)
    ? payload
    : [];

  return results
    .map((r) => {
      const providers = Array.isArray(r?.providers) ? r.providers : r && typeof r === "object" ? [r] : [];
      const normalizedProviders = providers.map((provider) =>
        normalizeProvider({
          ...provider,
          sku: provider?.sku ?? r?.sku ?? provider?.product_id,
        })
      );
      const best = pickBestProvider(normalizedProviders);
      if (!best?.title) return null;
      return {
        ...best,
        comparison: buildComparison(normalizedProviders),
      };
    })
    .filter(Boolean);
}

function normalizeProductsPayload(payload) {
  const data = payload;
  const catalogLike =
    Array.isArray(data) &&
    data.length === 1 &&
    data[0] &&
    typeof data[0] === "object" &&
    Array.isArray(data[0]?.products)
      ? data[0]
      : data && typeof data === "object" && Array.isArray(data?.products)
      ? data
      : null;

  const results = Array.isArray(catalogLike?.products)
    ? catalogLike.products
    : Array.isArray(data?.results)
    ? data.results
    : Array.isArray(data?.products)
    ? data.products
    : Array.isArray(data)
    ? data
    : [];

  const providers = (() => {
    if (Array.isArray(results) && results.length && Array.isArray(results[0]?.providers)) {
      return results.flatMap((group) => (Array.isArray(group?.providers) ? group.providers : []));
    }
    if (Array.isArray(results) && results.length && Array.isArray(results[0]?.results)) {
      return results.flatMap((group) => (Array.isArray(group?.results) ? group.results : []));
    }
    return results;
  })();

  const groupedBySku = new Map();
  providers.forEach((provider) => {
    const normalized = normalizeProvider(provider);
    const key = normalized.pid || normalized.sku || normalized.title;
    if (!key) return;
    if (!groupedBySku.has(key)) groupedBySku.set(key, []);
    groupedBySku.get(key).push(normalized);
  });

  return Array.from(groupedBySku.values())
    .map((group) => {
      const best = pickBestProvider(group);
      if (!best?.title) return null;
      return {
        ...best,
        comparison: buildComparison(group),
      };
    })
    .filter(Boolean);
}

export async function fetchPasteListMatches(lines) {
  const queryList = toQueryListParam(lines.join("\n"));
  if (!queryList) return [];

  const qs = new URLSearchParams();
  qs.set("query_list", queryList);
  qs.set("start", "0");
  qs.set("offset", String(PASTE_LIST_FEATURES_OFFSET));

  const featuresRes = await apiRequest(`api/actowiz/features?${qs.toString()}`, { method: "GET" });
  if (featuresRes.ok) {
    const products = normalizeFeatureResults(featuresRes.data);
    if (products.length) {
      return buildMatchesFromLines(lines, products);
    }
  }

  const productsQs = new URLSearchParams();
  productsQs.set("query_list", queryList);
  productsQs.set("start", "0");
  productsQs.set("offset", String(PASTE_LIST_FEATURES_OFFSET));
  const productsRes = await apiRequest(`api/actowiz/products?${productsQs.toString()}`, { method: "GET" });
  if (!productsRes.ok) {
    const errors = Array.isArray(productsRes.data?.errors) ? productsRes.data.errors : null;
    throw new Error(errors?.join("\n") || productsRes.data?.message || "Failed to match your list.");
  }

  const products = normalizeProductsPayload(productsRes.data);
  return buildMatchesFromLines(lines, products);
}

export function extractBasketPrices(payload) {
  if (!payload || typeof payload !== "object") return null;
  const prices = {
    asda: parsePrice(payload.asda_price),
    tesco: parsePrice(payload.tesco_price),
    morrison: parsePrice(payload.morrisons_price ?? payload.morrison_price),
    sainsbury: parsePrice(payload.sainsbury_price),
  };
  const hasAny = Object.values(prices).some((value) => Number.isFinite(value));
  return hasAny ? prices : null;
}

export function buildBasketTotalsFromApiPrices(source) {
  const prices =
    source &&
    (source.asda_price != null ||
      source.tesco_price != null ||
      source.morrisons_price != null ||
      source.morrison_price != null ||
      source.sainsbury_price != null)
      ? extractBasketPrices(source)
      : source;

  if (!prices) {
    return { rows: [], cheapest: null, savings: null, expensiveStore: null };
  }

  const rows = PLATFORM_ORDER.map((platform) => ({
    platform,
    label: PLATFORM_LABELS[platform] || platform,
    total: prices[platform],
    pricedCount: Number.isFinite(prices[platform]) ? 1 : 0,
  })).filter((row) => Number.isFinite(row.total) && row.total > 0);

  if (!rows.length) {
    return { rows: [], cheapest: null, savings: null, expensiveStore: null };
  }

  rows.sort((a, b) => a.total - b.total);
  const cheapest = rows[0];
  const expensive = rows[rows.length - 1];
  const savings = expensive.total - cheapest.total;
  const hasPriceSpread = savings > 0;

  return {
    rows: rows.map((row) => ({
      ...row,
      delta: row.total - cheapest.total,
      isBest: hasPriceSpread && row.total === cheapest.total,
    })),
    cheapest: hasPriceSpread ? cheapest : rows.length === 1 ? cheapest : null,
    savings: hasPriceSpread ? savings : null,
    expensiveStore: expensive,
  };
}

/** Normalize GET /grocery_lists/:id/calculate_basket_summary into basketTotals UI shape. */
export function normalizeGroceryBasketSummary(payload) {
  const root =
    payload?.data && typeof payload.data === "object" && !Array.isArray(payload.data)
      ? payload.data
      : payload;

  if (!root || typeof root !== "object") {
    return {
      basketTotals: { rows: [], cheapest: null, savings: null, expensiveStore: null },
      basketComparison: null,
    };
  }

  const basketComparison =
    root.basket_comparison && typeof root.basket_comparison === "object"
      ? root.basket_comparison
      : root.basketComparison && typeof root.basketComparison === "object"
        ? root.basketComparison
        : null;

  const rankingRows = Array.isArray(root.rankings)
    ? root.rankings
    : Array.isArray(root.rows)
      ? root.rows
      : null;

  if (rankingRows) {
    const mappedRows = rankingRows
      .map((row) => {
        const platform = normalizePlatformKey(row?.platform);
        if (!platform) return null;

        const complete = row?.complete !== false;
        const totalRaw = row?.total ?? row?.price ?? row?.amount;
        const total =
          typeof totalRaw === "string" && /incomplete/i.test(totalRaw)
            ? null
            : parsePrice(totalRaw);
        const pricedCount = Number(row?.priced_count ?? row?.pricedCount) || 0;
        const itemCount = Number(row?.item_count ?? row?.itemCount) || 0;
        const coverage = String(row?.coverage ?? row?.vs_best ?? "").trim() || null;
        const vsBest = String(row?.vs_best ?? "").trim() || coverage;
        const vsBestAmount = parsePrice(row?.vs_best_amount ?? row?.vsBestAmount);
        const rankRaw = row?.rank;
        const rank = rankRaw == null || rankRaw === "" ? null : Number(rankRaw);

        if (complete && (!Number.isFinite(total) || total <= 0)) return null;

        return {
          platform,
          label: row?.label || PLATFORM_LABELS[platform] || platform,
          total: complete && Number.isFinite(total) ? total : null,
          totalLabel: complete ? null : "Incomplete",
          complete,
          pricedCount,
          itemCount,
          coverage,
          vsBest,
          vsBestAmount: Number.isFinite(vsBestAmount) ? vsBestAmount : null,
          rank: Number.isFinite(rank) ? rank : null,
        };
      })
      .filter(Boolean);

    if (!mappedRows.length) {
      return {
        basketTotals: { rows: [], cheapest: null, savings: null, expensiveStore: null },
        basketComparison,
      };
    }

    const completeRows = mappedRows.filter((row) => row.complete && Number.isFinite(row.total));
    completeRows.sort((a, b) => {
      if (a.rank != null && b.rank != null && a.rank !== b.rank) return a.rank - b.rank;
      return a.total - b.total;
    });

    const incompleteRows = mappedRows.filter((row) => !row.complete || !Number.isFinite(row.total));
    const orderedRows = [...completeRows, ...incompleteRows];

    const cheapestTotal = completeRows.length ? completeRows[0].total : null;
    const expensive = completeRows.length ? completeRows[completeRows.length - 1] : null;
    const apiCheapestPlatform = normalizePlatformKey(root?.cheapest?.platform);
    const cheapestRow =
      (apiCheapestPlatform && completeRows.find((r) => r.platform === apiCheapestPlatform)) ||
      completeRows[0] ||
      null;
    const apiSavings = parsePrice(root?.savings);
    const computedSavings =
      cheapestTotal != null && expensive ? expensive.total - cheapestTotal : null;
    const savings =
      Number.isFinite(apiSavings) && apiSavings > 0
        ? apiSavings
        : computedSavings != null && computedSavings > 0
          ? computedSavings
          : null;
    const hasPriceSpread = savings != null && savings > 0;

    const basketTotals = {
      rows: orderedRows.map((row) => {
        if (!row.complete || !Number.isFinite(row.total)) {
          return {
            platform: row.platform,
            label: row.label,
            total: null,
            totalLabel: row.totalLabel || "Incomplete",
            complete: false,
            pricedCount: row.pricedCount,
            itemCount: row.itemCount,
            coverage: row.coverage,
            vsBest: row.vsBest,
            deltaLabel: row.vsBest || row.coverage || null,
            isBest: false,
          };
        }

        const isBest = hasPriceSpread
          ? row.total === cheapestTotal
          : completeRows.length === 1 && row.total === cheapestTotal;

        return {
          platform: row.platform,
          label: row.label,
          total: row.total,
          totalLabel: null,
          complete: true,
          pricedCount: row.pricedCount,
          itemCount: row.itemCount,
          coverage: row.coverage,
          vsBest: row.vsBest,
          delta: row.total - cheapestTotal,
          deltaLabel:
            isBest
              ? null
              : Number.isFinite(row.vsBestAmount) && row.vsBestAmount > 0
                ? `+£${row.vsBestAmount.toFixed(2)}`
                : row.vsBest && row.vsBest !== "+£0.00"
                  ? row.vsBest
                  : `+£${(row.total - cheapestTotal).toFixed(2)}`,
          isBest,
        };
      }),
      cheapest: cheapestRow
        ? {
            platform: cheapestRow.platform,
            label: cheapestRow.label,
            total: cheapestRow.total,
          }
        : null,
      savings: hasPriceSpread ? savings : null,
      expensiveStore:
        hasPriceSpread && expensive
          ? {
              platform: expensive.platform,
              label: expensive.label,
              total: expensive.total,
            }
          : null,
    };

    return { basketTotals, basketComparison };
  }

  // Flat asda_price / tesco_price style (same as features basket totals).
  return {
    basketTotals: buildBasketTotalsFromApiPrices(root),
    basketComparison,
  };
}

export { preparePasteListProductsForBasket };

export function groceryListIdOf(list) {
  return list?.id ?? list?.grocery_list_id ?? list?.list_id ?? null;
}

export function normalizeGroceryListsMinimal(data) {
  if (Array.isArray(data)) return data;
  if (Array.isArray(data?.data)) return data.data;
  return [];
}

export function groceryListProductsOf(detail) {
  const raw =
    detail?.products ||
    detail?.items ||
    detail?.grocery_list_products ||
    detail?.grocery_products ||
    [];
  return Array.isArray(raw) ? raw : [];
}

export async function fetchGroceryListDetailProducts(groceryListId) {
  const listId = groceryListId == null ? "" : String(groceryListId).trim();
  if (!listId) {
    throw new Error("Grocery list id is missing.");
  }

  const res = await apiRequest(`api/grocery_lists/${encodeURIComponent(listId)}`, { method: "GET" });
  if (!res.ok) {
    const errors = Array.isArray(res.data?.errors) ? res.data.errors : null;
    throw new Error(errors?.join("\n") || res.data?.message || "Failed to load grocery list.");
  }

  const detail = res.data?.data ?? res.data;
  return {
    detail,
    products: groceryListProductsOf(detail),
  };
}

export async function fetchGroceryBasketSummary(groceryListId) {
  const listId = groceryListId == null ? "" : String(groceryListId).trim();
  if (!listId) {
    return {
      basketTotals: { rows: [], cheapest: null, savings: null, expensiveStore: null },
      basketComparison: null,
    };
  }

  const res = await apiRequest(`api/grocery_lists/${encodeURIComponent(listId)}/calculate_basket_summary`, {
    method: "GET",
  });
  if (!res.ok) {
    const errors = Array.isArray(res.data?.errors) ? res.data.errors : null;
    const err = new Error(
      errors?.join("\n") || res.data?.message || "Failed to calculate basket summary."
    );
    err.status = res.status;
    err.data = res.data;
    throw err;
  }
  return normalizeGroceryBasketSummary(res.data);
}

export async function fetchPasteListBasketSummary(lines, { queryList } = {}) {
  const expanded = Array.isArray(lines)
    ? lines.map((line) => String(line || "").trim()).filter(Boolean)
    : expandPasteLines(lines);
  const resolvedQueryList =
    String(queryList || "").trim() || toQueryListParam(expanded.join("\n"));
  if (!resolvedQueryList) return null;

  const qs = new URLSearchParams();
  qs.set("query_list", resolvedQueryList);
  qs.set("start", "0");
  qs.set("offset", String(PASTE_LIST_FEATURES_OFFSET));

  const res = await apiRequest(`api/actowiz/features?${qs.toString()}`, { method: "GET" });
  if (!res.ok) return null;
  return buildBasketTotalsFromApiPrices(res.data);
}

export function calculateBasketTotals(matches) {
  const items = Array.isArray(matches) ? matches : [];
  const rows = PLATFORM_ORDER.map((platform) => ({
    platform,
    label: PLATFORM_LABELS[platform] || platform,
    total: 0,
    pricedCount: 0,
  }));

  items.forEach((match) => {
    const comparison = Array.isArray(match?.comparison)
      ? match.comparison
      : Array.isArray(match?.product?.comparison)
      ? match.product.comparison
      : Array.isArray(match?.platform_data)
      ? match.platform_data
      : Array.isArray(match?.product?.platform_data)
      ? match.product.platform_data
      : [];
    const qtyRaw = Number(match?.quantity ?? match?.product?.quantity ?? 1);
    const quantity = Number.isFinite(qtyRaw) && qtyRaw > 0 ? qtyRaw : 1;

    comparison.forEach((row) => {
      const platform = normalizePlatformKey(row?.platform);
      const target = rows.find((r) => r.platform === platform);
      if (!target) return;
      const basePrice = parsePrice(row?.price);
      const offerPrice = parsePrice(row?.offer_price ?? row?.offerPrice);
      const price =
        Number.isFinite(basePrice) && basePrice > 0
          ? basePrice
          : Number.isFinite(offerPrice) && offerPrice > 0
            ? offerPrice
            : null;
      if (Number.isFinite(price) && price > 0) {
        target.total += price * quantity;
        target.pricedCount += 1;
      }
    });
  });

  const itemCount = items.length;
  // Only rank stores that have a price for every item (complete basket).
  const pricedRows = rows.filter(
    (r) => itemCount > 0 && r.pricedCount === itemCount && r.total > 0
  );
  if (!pricedRows.length) {
    return { rows: [], cheapest: null, savings: null, expensiveStore: null };
  }

  pricedRows.sort((a, b) => a.total - b.total);
  const cheapest = pricedRows[0];
  const expensive = pricedRows[pricedRows.length - 1];
  const savings = expensive.total - cheapest.total;
  const hasPriceSpread = savings > 0;

  return {
    rows: pricedRows.map((row) => ({
      ...row,
      delta: row.total - cheapest.total,
      isBest: hasPriceSpread && row.total === cheapest.total,
    })),
    cheapest: hasPriceSpread ? cheapest : pricedRows.length === 1 ? cheapest : null,
    savings: hasPriceSpread ? savings : null,
    expensiveStore: expensive,
  };
}
